import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const EmployeeLoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Employee-specific mock credentials - Updated to clearequal.com domain
  const employeeCredentials = {
    'employee@clearequal.com': { 
      password: 'Employee123!', 
      role: 'employee', 
      name: 'John Doe',
      employeeId: 'EMP001',
      department: 'Engineering'
    },
    'jane.smith@clearequal.com': { 
      password: 'Jane123!', 
      role: 'employee', 
      name: 'Jane Smith',
      employeeId: 'EMP002',
      department: 'Marketing'
    },
    'mike.wilson@clearequal.com': { 
      password: 'Mike123!', 
      role: 'employee', 
      name: 'Mike Wilson',
      employeeId: 'EMP003',
      department: 'Sales'
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = 'Work email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid work email address';
    }
    
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    // Simulate API call with employee-specific authentication
    setTimeout(() => {
      const employee = employeeCredentials?.[formData?.email?.toLowerCase()];
      
      if (employee && employee?.password === formData?.password) {
        // Store employee session
        localStorage.setItem('user', JSON.stringify({
          email: formData?.email,
          role: employee?.role,
          name: employee?.name,
          employeeId: employee?.employeeId,
          department: employee?.department,
          loginTime: new Date()?.toISOString(),
          loginType: 'employee'
        }));
        
        if (formData?.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('trustedDevice', JSON.stringify({
            deviceId: 'emp-device-' + Date.now(),
            registeredAt: new Date()?.toISOString(),
            email: formData?.email
          }));
        }
        
        // Navigate to employee portal
        navigate('/employee-portal');
      } else {
        setErrors({ 
          general: 'Invalid employee credentials. Please check your work email and password.' 
        });
      }
      
      setIsLoading(false);
    }, 1500);
  };

  const handleForgotPassword = () => {
    // Employee-specific password reset
    alert('Password reset instructions will be sent to your work email. Please contact HR if you need additional assistance.');
  };

  const handleDeviceRecognition = () => {
    // Check for trusted device
    const trustedDevice = localStorage.getItem('trustedDevice');
    if (trustedDevice) {
      const device = JSON.parse(trustedDevice);
      if (device?.email === formData?.email) {
        setFormData(prev => ({ ...prev, rememberMe: true }));
      }
    }
  };

  React.useEffect(() => {
    if (formData?.email) {
      handleDeviceRecognition();
    }
  }, [formData?.email]);

  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors?.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-red-600" />
              <p className="text-sm text-red-700">{errors?.general}</p>
            </div>
          </div>
        )}

        {/* Employee-specific trust message */}
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md">
          <div className="flex items-start space-x-2">
            <Icon name="Shield" size={16} className="text-emerald-600 mt-0.5" />
            <div className="text-sm text-emerald-700">
              <p className="font-medium mb-1">{t('employeeLoginPage.secureEmployeeAccess')}</p>
              <p>Your compensation data is protected and only accessible to you under EU privacy laws.</p>
            </div>
          </div>
        </div>

        <Input
          label="Work Email Address"
          type="email"
          name="email"
          placeholder="Enter your company email"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          disabled={isLoading}
        />

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
            disabled={isLoading}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <Checkbox
            label="Remember this device"
            name="rememberMe"
            checked={formData?.rememberMe}
            onChange={handleInputChange}
            disabled={isLoading}
          />
          
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm text-emerald-600 hover:text-emerald-700 transition-smooth"
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          Access Employee Portal
        </Button>

        {/* Mobile-optimized access note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            💱 Optimized for mobile access on personal devices
          </p>
        </div>
      </form>
    </div>
  );
};

export default EmployeeLoginForm;